import { Asterisk, LockKeyhole, LockKeyholeOpen } from 'lucide-react'
import { useRef } from 'react'
import type { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

const columns =
  'grid grid-cols-[40px_minmax(0,1fr)] items-center gap-x-2 gap-y-1 @[460px]/fields:grid-cols-[40px_minmax(140px,0.85fr)_minmax(0,1.15fr)]'
function FieldToggle({
  label,
  active,
  disabled,
  tooltip,
  onToggle,
  children,
}: {
  label: string
  active: boolean
  disabled: boolean
  tooltip: string
  onToggle?: () => void
  children: ReactNode
}) {
  const controlClass = cn(
    'flex h-8 w-5 items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    active ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground/50'
  )
  if (!onToggle && !active)
    return <span aria-hidden="true" className={controlClass} />

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {onToggle ? (
          <span className="inline-flex">
            <button
              type="button"
              aria-label={label}
              aria-pressed={active}
              disabled={disabled}
              onClick={onToggle}
              className={cn(
                controlClass,
                'cursor-pointer enabled:hover:bg-muted/70 disabled:cursor-default'
              )}>
              {children}
            </button>
          </span>
        ) : (
          <span tabIndex={0} aria-label={label} className={controlClass}>
            {children}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

export function SigningRow({
  id,
  name,
  label,
  required,
  locked,
  selected,
  previewing = false,
  onSelect,
  onRequiredChange,
  onManagedChange,
  children,
}: {
  id: string
  name: string
  label: string
  required: boolean
  locked: boolean
  selected: boolean
  previewing?: boolean
  onSelect: (toggle?: boolean) => void
  onRequiredChange?: (required: boolean) => void
  onManagedChange?: (managed: boolean) => void
  children: ReactNode
}) {
  const pointerSelecting = useRef(false)
  const OwnershipIcon = locked ? LockKeyhole : LockKeyholeOpen
  return (
    <div
      id={id}
      data-field={name}
      data-selected={selected}
      onPointerDownCapture={(event) => {
        // Portaled source menus bubble through the React tree, but are not row clicks.
        if (!event.currentTarget.contains(event.target as Node)) return
        pointerSelecting.current = true
        onSelect(true)
      }}
      onPointerUpCapture={() => {
        pointerSelecting.current = false
      }}
      onPointerCancel={() => {
        pointerSelecting.current = false
      }}
      onFocus={(event) => {
        if (
          !pointerSelecting.current &&
          event.currentTarget.contains(event.target as Node)
        )
          onSelect()
      }}
      className={cn(
        columns,
        'cursor-pointer border-b border-border/50 px-2 py-2.5 transition-colors last:border-b-0',
        selected
          ? 'bg-blue-100/80 ring-1 ring-inset ring-blue-400/70 dark:bg-blue-950/50'
          : 'hover:bg-muted/30'
      )}>
      <div className="flex items-center">
        <FieldToggle
          label={`${label}: ${onRequiredChange || required ? 'required' : 'optional'}`}
          active={required}
          disabled={previewing || !onRequiredChange}
          tooltip={
            previewing || !onRequiredChange
              ? required
                ? 'Required'
                : 'Optional'
              : required
                ? 'Make optional'
                : 'Make required'
          }
          onToggle={
            onRequiredChange ? () => onRequiredChange(!required) : undefined
          }>
          <Asterisk className="size-3.5" />
        </FieldToggle>
        <FieldToggle
          label={`${label}: ${onManagedChange ? 'locked' : locked ? 'managed by Careforce' : 'configurable by organization'}`}
          active={locked}
          disabled={previewing || !onManagedChange}
          tooltip={
            previewing || !onManagedChange
              ? locked
                ? 'Managed by Careforce'
                : 'Configurable by organization'
              : locked
                ? 'Allow organizations to configure'
                : 'Manage this value in Careforce'
          }
          onToggle={
            onManagedChange ? () => onManagedChange(!locked) : undefined
          }>
          <OwnershipIcon className="size-3" />
        </FieldToggle>
      </div>
      <div className="min-w-0">
        <button
          type="button"
          onClick={(event) => {
            if (event.detail === 0) onSelect(true)
          }}
          className="block cursor-pointer text-left text-sm font-medium leading-5 hover:underline">
          {label}
        </button>
      </div>
      <div className="col-start-2 min-w-0 @[460px]/fields:col-start-auto">
        {children}
      </div>
    </div>
  )
}
